import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../components/Modal';
import { ValueChange } from '../../components/ui/ValueChange';
import { fetchQuotationVersions, type QuotationVersionDto } from '../../lib/quotationApi';
// The line's selling price comes from the mirrored owner, exactly as the grid prices it. A
// comparison that derived selling prices its own way would disagree with the screen beside it.
import { resolveItemPricing } from '../../utils/pricing';
import { formatCurrency } from '../../utils/priceUtils';

/**
 * Compare two versions of one quotation (D45.2) — normally the version open on the page against
 * the version the client was SENT, which is kept exactly as they saw it.
 *
 * Ported from om-shipment-Tidal's modal, on USC's owners: lines are priced by
 * `utils/pricing.resolveItemPricing` at the page's markup, and every amount is the quotation's
 * currency of record, US$ (`unitPrice` and the selling prices are stored in US$).
 */
interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** The version open on the page — always one side of the comparison. */
  quotationId: string;
  /** The page's house markup, so a line with no rate of its own prices as the grid shows it. */
  defaultMarkupPct: number;
}

export interface PricedVersionLine {
  lineNumber: number | null;
  itemName: string;
  quantity: number | null;
  uom: string;
  unitCost: number;
  unitSelling: number;
  totalSelling: number;
}

export interface CompareRow {
  key: string;
  itemName: string;
  older?: PricedVersionLine;
  newer?: PricedVersionLine;
}

const norm = (s?: string | null) => String(s || '').trim().toLowerCase();

/** "Original" for the root, "Version N" for the rest. */
export const versionLabel = (v: Pick<QuotationVersionDto, 'revisionNumber'>) =>
  v.revisionNumber ? `Version ${v.revisionNumber}` : 'Original';

export function priceVersion(version: QuotationVersionDto, defaultMarkupPct: number): PricedVersionLine[] {
  return (version.items || []).map((it) => {
    const priced = resolveItemPricing(it, defaultMarkupPct);
    return {
      lineNumber: it.lineNumber ?? null,
      itemName: it.itemName || '',
      quantity: typeof it.quantity === 'number' ? it.quantity : null,
      uom: it.uom || '',
      unitCost: priced.unitCost,
      unitSelling: priced.unitSellingPrice,
      totalSelling: priced.totalSellingPrice,
    };
  });
}

/**
 * Pair the two versions' lines, most certain match first: the same number AND the same item, then
 * the same item (a line deleted before it renumbers everything after), then the same number (an
 * item renamed in place). Anything still unpaired was added or removed.
 */
export function pairLines(olderLines: PricedVersionLine[], newerLines: PricedVersionLine[]): CompareRow[] {
  const unmatched = [...olderLines];
  const partner = new Map<number, PricedVersionLine>();
  const sameName = (a: PricedVersionLine, b: PricedVersionLine) => norm(a.itemName) !== '' && norm(a.itemName) === norm(b.itemName);
  const sameNumber = (a: PricedVersionLine, b: PricedVersionLine) => a.lineNumber != null && a.lineNumber === b.lineNumber;
  const passes: Array<(o: PricedVersionLine, n: PricedVersionLine) => boolean> = [
    (o, n) => sameNumber(o, n) && sameName(o, n),
    sameName,
    sameNumber,
  ];
  for (const matches of passes) {
    newerLines.forEach((newer, idx) => {
      if (partner.has(idx)) return;
      const i = unmatched.findIndex((older) => matches(older, newer));
      if (i >= 0) partner.set(idx, unmatched.splice(i, 1)[0]);
    });
  }

  const rows: CompareRow[] = newerLines.map((newer, idx) => ({
    key: `n${newer.lineNumber ?? idx}-${newer.itemName}`,
    itemName: newer.itemName,
    older: partner.get(idx),
    newer,
  }));
  unmatched.forEach((older, idx) => {
    rows.push({ key: `o${older.lineNumber ?? idx}-${older.itemName}`, itemName: older.itemName, older });
  });
  return rows;
}

export const rowChanged = (r: CompareRow) =>
  !r.older ||
  !r.newer ||
  r.older.quantity !== r.newer.quantity ||
  norm(r.older.uom) !== norm(r.newer.uom) ||
  r.older.unitCost !== r.newer.unitCost ||
  r.older.unitSelling !== r.newer.unitSelling;

// "US$", never a bare "$" (client 2026-09-26) — the owner labels it.
const usd = (value: number) => formatCurrency(value, 'USD');
const qtyText = (l?: PricedVersionLine) => (l ? `${l.quantity ?? '—'}${l.uom ? ` ${l.uom}` : ''}` : undefined);

function Pair({ from, to }: { from?: string; to?: string }) {
  if (from != null && to != null && from === to) return <span className="tabular-nums text-slate-600">{to}</span>;
  if (to == null) {
    return (
      <del className="tabular-nums text-slate-500">
        <span className="sr-only">was </span>
        {from}
      </del>
    );
  }
  return <ValueChange from={from} to={to} />;
}

/** The version compared against by default: the newest SENT one other than this, else the one before. */
export function defaultOtherVersionId(chain: QuotationVersionDto[], quotationId: string): string | null {
  const others = chain.filter((v) => v._id !== quotationId);
  const sent = others.filter((v) => v.status === 'sent').sort((a, b) => b.revisionNumber - a.revisionNumber);
  if (sent.length) return sent[0]._id;
  const idx = chain.findIndex((v) => v._id === quotationId);
  const neighbour = chain[idx - 1] || chain[idx + 1];
  return neighbour ? neighbour._id : null;
}

export const RevisionCompareModal: React.FC<Props> = ({ isOpen, onClose, quotationId, defaultMarkupPct }) => {
  const [chain, setChain] = useState<QuotationVersionDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [otherId, setOtherId] = useState<string | null>(null);
  const [changedOnly, setChangedOnly] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchQuotationVersions(quotationId)
      .then((data) => {
        if (cancelled) return;
        setChain(data.versions);
        setOtherId(defaultOtherVersionId(data.versions, quotationId));
      })
      .catch(() => {
        if (!cancelled) setError('The versions of this quotation could not be loaded.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, quotationId, attempt]);

  const view = useMemo(() => {
    if (!chain) return null;
    const current = chain.find((v) => v._id === quotationId);
    const other = chain.find((v) => v._id === otherId);
    if (!current || !other) return null;
    // Ordered by version, never by which one is open, so the left side is always the older one.
    const [older, newer] = current.revisionNumber <= other.revisionNumber ? [current, other] : [other, current];
    const rows = pairLines(priceVersion(older, defaultMarkupPct), priceVersion(newer, defaultMarkupPct));
    const totalOlder = rows.reduce((sum, r) => sum + (r.older?.totalSelling || 0), 0);
    const totalNewer = rows.reduce((sum, r) => sum + (r.newer?.totalSelling || 0), 0);
    return { older, newer, rows, totalOlder, totalNewer };
  }, [chain, quotationId, otherId, defaultMarkupPct]);

  const visibleRows = view ? view.rows.filter((r) => !changedOnly || rowChanged(r)) : [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Compare versions" maxWidthClass="max-w-5xl">
      {loading ? (
        <div className="py-10 text-center text-sm text-slate-500">Loading versions…</div>
      ) : error ? (
        <div className="py-10 text-center text-sm text-red-700" role="alert">
          {error}{' '}
          <button type="button" className="font-semibold underline" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </div>
      ) : !chain || chain.length < 2 ? (
        <div className="py-10 text-center text-sm text-slate-500">This quotation has no other version to compare with.</div>
      ) : !view ? (
        <div className="py-10 text-center text-sm text-slate-500">Pick a version to compare with.</div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 pb-3 text-xs text-slate-600">
            <label>
              Compare with{' '}
              <select
                value={otherId || ''}
                onChange={(e) => setOtherId(e.target.value)}
                className="ml-1 rounded-md border border-slate-200 bg-white px-1.5 py-1 text-xs text-slate-700"
              >
                {chain
                  .filter((v) => v._id !== quotationId)
                  .map((v) => (
                    <option key={v._id} value={v._id}>
                      {v.quotationNumber} — {versionLabel(v)}
                      {v.status === 'sent' ? ' (sent)' : ''}
                    </option>
                  ))}
              </select>
            </label>
            <label className="flex cursor-pointer items-center gap-1.5">
              <input type="checkbox" checked={changedOnly} onChange={(e) => setChangedOnly(e.target.checked)} />
              Only lines that changed
            </label>
            <span className="ml-auto">
              {view.older.quotationNumber} → {view.newer.quotationNumber}:{' '}
              <span className="font-semibold tabular-nums text-slate-800">{usd(view.totalNewer - view.totalOlder)}</span> on the
              selling total
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-xs text-slate-500">
                  <th className="py-2 pr-3 font-semibold">Item</th>
                  <th className="px-3 py-2 font-semibold">Quantity</th>
                  <th className="px-3 py-2 text-right font-semibold">Unit cost</th>
                  <th className="px-3 py-2 text-right font-semibold">Unit selling</th>
                  <th className="py-2 pl-3 text-right font-semibold">Total selling</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={row.key} className="border-t border-slate-100 align-top">
                    <td className="py-2 pr-3 text-slate-800">
                      {row.itemName || '—'}
                      {!row.older && (
                        <span className="ml-2 rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-800">Added</span>
                      )}
                      {!row.newer && (
                        <span className="ml-2 rounded bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-800">Removed</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Pair from={qtyText(row.older)} to={qtyText(row.newer)} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Pair from={row.older && usd(row.older.unitCost)} to={row.newer && usd(row.newer.unitCost)} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Pair from={row.older && usd(row.older.unitSelling)} to={row.newer && usd(row.newer.unitSelling)} />
                    </td>
                    <td className="py-2 pl-3 text-right">
                      <Pair from={row.older && usd(row.older.totalSelling)} to={row.newer && usd(row.newer.totalSelling)} />
                    </td>
                  </tr>
                ))}
                {visibleRows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-sm text-slate-500">
                      No line changed between {view.older.quotationNumber} and {view.newer.quotationNumber}.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-300 font-semibold text-slate-800">
                  <td className="py-2 pr-3" colSpan={4}>
                    Selling total
                  </td>
                  <td className="py-2 pl-3 text-right">
                    <Pair from={usd(view.totalOlder)} to={usd(view.totalNewer)} />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="pt-3 text-xs text-slate-500">
            Amounts in US$, the currency every quotation line is stored in, before discount and charges.
          </p>
        </>
      )}
    </Modal>
  );
};

export default RevisionCompareModal;
